import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../context/AuthContext";
import LeftArrow from "../../assests/left-arrow.svg";
import api from "../../services";
import MapWithSearch from "../../MapWithSearch";
import VideoLoader from "../../components/VideoLoader";
import { genrateOrderQuote } from "../../services/apis";
import toast from "react-hot-toast";
import ErrorHandlerPage from "../../components/ErrorHandlerPage";
interface AddressItem {
  _id?: string;
  type: "Home" | "Office" | "Others";
  tag?: string;
  FlatBlock?: string;
  Area?: string;
  latitude?: number;
  longitude?: number;
}

export const Address: React.FC = () => {
  const [addresses, setAddresses] = useState<AddressItem[]>([]);
  const [isAddressModalOpen, setIsAddressModalOpen] = useState(false);
  const [isEditOpen, setEditModalOpen] = useState(false);
  const [deliverable, setDeliverable] = useState(false);
  const { user, isAuth } = useAuth();
  const [deliveryStautsLoading, setDeliveryStatusLoading] = useState(false);
  const navigate = useNavigate();
  const [isFormModified, setIsFormModified] = useState(false);
  // Address form states
  const [flatBlock, setFlatBlock] = useState("");
  const [area, setArea] = useState("");
  const [latitude, setLatitude] = useState(0);
  const [longitude, setLongitude] = useState(0);
  const [tag, setTag] = useState("");
  const [fullMapAddress, setFullMapAddress] = useState("");
  const [editingAddressId, setEditingAddressId] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [fatalError, setFatalError] = useState<{
    title: string;
    description: string;
    showBackToAddresses?: boolean;
    showBackToHome?: boolean;
  } | null>(null);
  type ButtonType = "Home" | "Office" | "Others";
  const [selectedButton, setSelectedButton] = useState<ButtonType | "">("");

  const handleButtonClick = (button: ButtonType) => {
    if (selectedButton === button) {
      setSelectedButton("");
      setTag("");
    } else {
      setSelectedButton(button);
      setTag(button);
    }
    setIsFormModified(true);
  };

  // Handle map location change
  const handleLocationChange = (data: {
    address: string;
    lat: number;
    lng: number;
    flatBlock?: string;
    area?: string;
    fullAddress?: string;
  }) => {
    const fullAddress = data.fullAddress || data.address || "";
    const addressParts = fullAddress
      .split(",")
      .map((part) => part.trim())
      .filter(Boolean);
    const normalizedFlatBlock = (data.flatBlock || "").trim().toLowerCase();
    const normalizedArea = (data.area || "").trim().toLowerCase();
    const derivedArea =
      addressParts.length > 1 ? addressParts[1] : addressParts[0] || "";
    const Area =
      data.area && normalizedArea !== normalizedFlatBlock
        ? data.area
        : derivedArea;

    setLatitude(data.lat);
    setLongitude(data.lng);
    if (fullAddress) setFullMapAddress(fullAddress);
    if (data.flatBlock) setFlatBlock(data.flatBlock);
    setArea(Area);
    generateOrderQuote(fullAddress, data?.lat, data?.lng);
    setIsFormModified(true);
  };

  const fetchUserData = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get(`${process.env.REACT_APP_API_URL}/address`);
      setAddresses(res.data.addresses);
      setFatalError(null);
    } catch (error) {
      console.error("Error fetching address", error);
      setFatalError({
        title: "Addresses Unavailable",
        description:
          "We are unable to load your saved addresses at the moment.",
        showBackToAddresses: false,
        showBackToHome: true,
      });
    } finally {
      setLoading(false);
    }
  }, []);

  // Fetch user addresses
  useEffect(() => {
    if (!user?.id || !isAuth) {
      navigate("/");
      return;
    }

    fetchUserData();
  }, [user?.id, isAuth, navigate, fetchUserData]);

  const resetForm = () => {
    setFlatBlock("");
    setArea("");
    setLatitude(0);
    setLongitude(0);
    setSelectedButton("");
    setTag("");
    setFullMapAddress("");
    setEditingAddressId(null);
  };

  const buildAreaPayload = () => {
    const normalizedFlatBlock = flatBlock.trim().toLowerCase();
    const normalizedArea = area.trim();
    const mapParts = (fullMapAddress || "")
      .split(",")
      .map((part) => part.trim())
      .filter(Boolean);

    if (mapParts.length > 0) {
      if (
        normalizedFlatBlock &&
        mapParts[0].toLowerCase() === normalizedFlatBlock &&
        mapParts.length > 1
      ) {
        return mapParts.slice(1).join(", ");
      }

      if (normalizedArea) {
        const areaIndex = mapParts.findIndex(
          (part) => part.toLowerCase() === normalizedArea.toLowerCase(),
        );
        if (areaIndex >= 0) {
          return mapParts.slice(areaIndex).join(", ");
        }
      }

      if (mapParts.length > 1) {
        return mapParts.slice(1).join(", ");
      }

      return mapParts[0];
    }

    return normalizedArea;
  };
  // Edit existing address
  const handleEditClick = (addr: AddressItem) => {
    setEditingAddressId(addr._id || null);
    setFlatBlock(addr.FlatBlock || "");
    setArea(addr.Area || "");
    setLatitude(addr.latitude || 0);
    setLongitude(addr.longitude || 0);
    setSelectedButton(addr.type);
    setTag(addr.tag || "");
    setEditModalOpen(true);
    setIsFormModified(false);
    if (addr.Area && addr.latitude && addr.longitude) {
      generateOrderQuote(addr.FlatBlock+", "+addr.Area, addr.latitude, addr.longitude);
    }
  };

  // Delete address
  const handleDelete = async (id?: string) => {
    if (!id) return;
    try {
      await api.delete(`${process.env.REACT_APP_API_URL}/address/${id}`);
      setAddresses((prev) => prev.filter((a) => a._id !== id));
    } catch (error) {
      console.error("Error deleting address", error);
      setFatalError({
        title: "Unable to Delete Address",
        description: "Your address could not be deleted at this time.",
        showBackToAddresses: true,
      });
    }
  };

  // Add new address
  const handleAddressSubmit = async () => {
    if (!flatBlock || !area || !selectedButton) {
      toast.error("Please fill out all fields");
      return;
    }
    if (!latitude || !longitude) {
      toast.error("Please select a location on the map");
      return;
    }

    const combinedAddress = buildAreaPayload();
    const newAddress = {
      userId: user?.id,
      type: selectedButton,
      tag: selectedButton === "Others" ? tag : "",
      FlatBlock: flatBlock,
      Area: combinedAddress,
      latitude,
      longitude,
    };

    try {
      const response = await api.post(
        `${process.env.REACT_APP_API_URL}/address`,
        newAddress,
      );
      setAddresses((prev) => [...prev, response.data.address]);
      resetForm();
      setIsAddressModalOpen(false);
    } catch (error) {
      console.error("Error adding address:", error);
      setFatalError({
        title: "Unable to Add Address",
        description: "Your address could not be saved at this time.",
        showBackToAddresses: true,
      });
    }
  };

  // Update address
  const handleEditAddress = async () => {
    if (!editingAddressId) return;
    if (!flatBlock || !area || !selectedButton) {
      toast.error("Please fill out all fields");
      return;
    }

    const combinedAddress = buildAreaPayload();
    const updatedAddress = {
      type: selectedButton,
      tag: selectedButton === "Others" ? tag : "",
      FlatBlock: flatBlock,
      Area: combinedAddress,
      latitude,
      longitude,
    };

    try {
      const response = await api.put(
        `${process.env.REACT_APP_API_URL}/address/${editingAddressId}`,
        updatedAddress,
      );
      setAddresses((prev) =>
        prev.map((addr) =>
          addr._id === editingAddressId ? response.data.address : addr,
        ),
      );
      resetForm();
      setEditModalOpen(false);
    } catch (error) {
      console.error("Error updating address:", error);
      setFatalError({
        title: "Unable to Update Address",
        description: "Your address could not be updated at this time.",
        showBackToAddresses: true,
      });
    }
  };
  if (fatalError) {
    return (
      <ErrorHandlerPage
        title={fatalError.title}
        description={fatalError.description}
        retryLabel="Retry"
        showHomeButton={Boolean(
          fatalError.showBackToAddresses || fatalError.showBackToHome,
        )}
        homeLabel={
          fatalError.showBackToHome ? "Back to Home" : "Back to Addresses"
        }
        onHome={() => {
          setFatalError(null);
          if (fatalError.showBackToHome) {
            navigate("/");
            return;
          }
          fetchUserData();
        }}
        onRetry={() => {
          setFatalError(null);
          fetchUserData();
        }}
      />
    );
  }

  if (loading) {
    return (
      <div className="fixed inset-0 flex items-center justify-center bg-white z-50 m-0">
        <VideoLoader size="medium" />
      </div>
    );
  }

  const generateOrderQuote = async (
    dropoff_address: string,
    dropoff_latitude: number,
    dropoff_longitude: number,
  ) => {
    try {
      setDeliveryStatusLoading(true);
      setDeliverable(false);
      const res = await genrateOrderQuote({
        dropoff_address,
        dropoff_latitude,
        dropoff_longitude,
        pickup_time: new Date().toISOString(),
      });
      if (res.data.success) {
        console.log(res.data, "order qutote");

        if (res.data.quotes.uber.success || res.data.quotes.doordash.success) {
          setDeliverable(true);
        } else {
          toast.error(`The specified location is not in a deliverable area.`);
        }
      } else {
        setDeliverable(false);
        toast.error("Unable to verify delivery availability at this time.");
      }
      setDeliveryStatusLoading(false);
    } catch (error) {
      console.log("Error generating order quote");
      toast.error(`The specified location is not in a deliverable area.`);
      setDeliveryStatusLoading(false);
      setDeliverable(false);
    }
  };

  return (
    <>
      <div className="bg-white min-h-screen font-sans">
        <div className="bg-white  px-6 py-4 ">
          <div className="max-w-4xl mx-auto px-2">
            <div className="flex items-center gap-4">
              <button
                className="p-2 rounded-full bg-transparent items-center flex justify-center w-[35px] h-[35px] border-[1px] border-black/20  text-black bg-white text-lg font-bold  transition"
                onClick={() => navigate(-1)}
              >
                {/* ← */}
                <img
                  src={LeftArrow}
                  alt="Back"
                  className="w-4 h-4 dm-icon-invert"
                />
              </button>
              <span className="text-[20px] font-bold text-black">
                My Addresses
              </span>
            </div>
          </div>
        </div>
        <div className="text-black p-6 sm:p-2">
          <div className="max-w-4xl mx-auto sm:p-4 ">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3 sm:p-5 bg-white text-black">
              {addresses.length > 0 &&
                addresses.map((addr) => (
                  <div
                    key={addr._id}
                    className="p-4 border border-black/20 rounded-2xl transition-transform duration-300 h-[170px] flex flex-col justify-between"
                  >
                    {/* Top content */}
                    <div>
                      <div className="flex justify-between items-start">
                        <span className="font-bold text-[19px] text-black">
                          {addr.type === "Others" ? addr.tag : addr.type}
                        </span>
                      </div>
                      <p className="line-clamp-3 text-[13px] text-gray-600 mt-1">
                        {addr.FlatBlock}, {addr.Area}
                      </p>
                    </div>

                    {/* Bottom buttons */}
                    <div className="flex justify-between gap-4 mt-2">
                      <button
                        className="text-red-600 text-[15px] font-bold"
                        onClick={() => handleDelete(addr._id)}
                      >
                        Delete
                      </button>
                      <button
                        className="text-black text-[15px] font-bold"
                        onClick={() => handleEditClick(addr)}
                      >
                        Edit
                      </button>
                    </div>
                  </div>
                ))}

              {/* Add Address Card */}
              <button
                onClick={() => {
                  resetForm();
                  setIsAddressModalOpen(true);
                }}
                className="relative p-4 border border-black/20 rounded-2xl flex flex-col items-center justify-center text-gray-600 transition h-[170px]"
              >
                <span className="text-2xl font-bold">＋</span>
                <span className="text-sm font-medium">
                  Add delivery address
                </span>
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Add Address Modal */}
      {isAddressModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 m-0">
          <div className="dm-address-modal bg-white rounded-lg w-[90%] max-w-md p-6 relative flex flex-col border border-black/20">
            <button
              className="absolute top-3 right-3 text-gray-600 hover:text-gray-700"
              onClick={() => {
                setIsAddressModalOpen(false);
                resetForm();
              }}
            >
              ✕
            </button>

            <h3 className="dm-address-text text-lg font-semibold mb-2 text-black">
              Add New Address
            </h3>
            <MapWithSearch onLocationChange={handleLocationChange} />

            <input
              type="text"
              placeholder="FLAT/BLOCK NUMBER"
              className="dm-address-input w-full p-2.5 mt-2 text-sm border border-black/20 rounded text-black"
              value={flatBlock}
              onChange={(e) => setFlatBlock(e.target.value)}
            />

            <input
              type="text"
              placeholder="Area"
              className="dm-address-input w-full p-2.5 mt-2 text-sm border border-black/20 rounded text-black"
              value={area}
              onChange={(e) => setArea(e.target.value)}
            />

            <div className="flex gap-2 mt-3">
              {(["Home", "Office", "Others"] as ButtonType[]).map((button) => (
                <button
                  key={button}
                  className={`dm-address-tag flex-1 h-11 rounded-sm border flex items-center justify-center transition-colors ${
                    selectedButton === button
                      ? "dm-address-tag-selected"
                      : "dm-address-tag-unselected"
                  }`}
                  onClick={() => handleButtonClick(button)}
                >
                  {button}
                </button>
              ))}
            </div>

            {selectedButton === "Others" && (
              <input
                type="text"
                placeholder="Enter Tag"
                value={tag}
                onChange={(e) => setTag(e.target.value)}
                className="dm-address-input w-full p-2.5 mt-2 text-sm border border-black/20 rounded text-black"
              />
            )}

            <button
              className="dm-address-cta mt-4 w-full bg-main/90 text-white py-2 px-4 rounded"
              onClick={handleAddressSubmit}
              disabled={!deliverable}
            >
              Save Address
            </button>
          </div>
        </div>
      )}

      {/* Edit Address Modal */}
      {isEditOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 m-0">
          <div className="dm-address-modal bg-white rounded-lg w-[90%] max-w-md p-6 relative flex flex-col border border-black/20">
            <button
              className="absolute top-3 right-3 text-gray-600 hover:text-gray-700"
              onClick={() => {
                setEditModalOpen(false);
                resetForm();
              }}
            >
              ✕
            </button>

            <h3 className="dm-address-text text-lg font-semibold mb-2 text-black">
              Edit Address
            </h3>
            <MapWithSearch
              onLocationChange={handleLocationChange}
              editAddress={{
                address: flatBlock + " " + area,
                lat: latitude,
                lng: longitude,
              }}
            />

            <input
              type="text"
              placeholder="FLAT/BLOCK NUMBER"
              className="dm-address-input w-full p-2.5 mt-2 text-sm border border-black/20 rounded text-black"
              value={flatBlock}
              onChange={(e) => {
                setFlatBlock(e.target.value);
                setIsFormModified(true);
              }}
            />

            <input
              type="text"
              placeholder="Area"
              className="dm-address-input w-full p-2.5 mt-2 text-sm border border-black/20 rounded text-black"
              value={area}
              onChange={(e) => setArea(e.target.value)}
              disabled
            />

            <div className="flex gap-2 mt-3">
              {(["Home", "Office", "Others"] as ButtonType[]).map((button) => (
                <button
                  key={button}
                  className={`dm-address-tag flex-1 h-11 rounded-sm border flex items-center justify-center transition-colors ${
                    selectedButton === button
                      ? "dm-address-tag-selected"
                      : "dm-address-tag-unselected"
                  }`}
                  onClick={() => handleButtonClick(button)}
                >
                  {button}
                </button>
              ))}
            </div>

            {selectedButton === "Others" && (
              <input
                type="text"
                placeholder="Enter Tag"
                value={tag}
                onChange={(e) => {
                  setTag(e.target.value);
                  setIsFormModified(true);
                }}
                className="dm-address-input w-full p-2.5 mt-2 text-sm border border-black/20 rounded text-black"
              />
            )}
            <button
              className="dm-address-cta mt-4 w-full bg-main/90 text-white py-2 px-4 rounded"
              onClick={handleEditAddress}
              disabled={!deliverable || !isFormModified}
            >
              Update Address
            </button>
          </div>
        </div>
      )}
    </>
  );
};

export default Address;
